/**
 * useSmartCart Hook
 * 
 * Centralized cart logic that automatically handles:
 * - Authenticated users: Database + localStorage backup
 * - Non-authenticated users: localStorage only
 * - Smart routing between persistence methods
 * 
 * MVP-friendly: Simple interface for single developer maintenance
 */

import { useCallback } from 'react';
import { logger } from '@/utils/logger';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToCartAsync,
  updateCartItemAsync,
  removeFromCartAsync,
  clearCartAsync,
  addToCartLocal,
  updateQuantityLocal,
  removeFromCartLocal,
  clearCart,
  updateQuantityOptimistic,
  updateQuantitySuccess,
  updateQuantityFailure
} from '../store/slices/cartSlice';
import { localCartService } from '../services/localCartService';

export const useSmartCart = () => {
  const dispatch = useDispatch();
  const { user, is_authenticated } = useSelector(state => state.auth);
  const { items, loading, error, updating_items: updatingItems } = useSelector(state => state.cart);

  /**
   * Add item to cart with smart routing
   * @param {Object} artwork - Artwork object
   * @param {number} quantity - Quantity to add
   */
  /**
   * Get artist_id from a cart item (handles multiple data shapes)
   */
  const getItemArtistId = useCallback((item) => {
    return item.artwork?.artist_id || item.artist_id || item.artwork?.artist?.id || null;
  }, []);

  /**
   * Get artist display name from a cart item
   */
  const getItemArtistName = useCallback((item) => {
    return item.artwork?.artist?.full_name || item.artist_name || item.artwork?.artist_name || 'otro artista';
  }, []);

  /**
   * Check if adding this artwork would mix artists in the cart.
   * Returns { conflict: false } if OK, or { conflict: true, current_artist_name } if blocked.
   */
  const checkArtistConflict = useCallback((artwork) => {
    if (!items || items.length === 0) return { conflict: false };

    const current_artist_id = getItemArtistId(items[0]);
    const new_artist_id = artwork.artist_id || artwork.artist?.id;

    if (!current_artist_id || !new_artist_id) return { conflict: false };

    if (current_artist_id === new_artist_id) return { conflict: false };

    return {
      conflict: true,
      current_artist_name: getItemArtistName(items[0]),
      current_artist_id,
    };
  }, [items, getItemArtistId, getItemArtistName]);

  /**
   * Add item to cart with smart routing.
   * Returns { conflict: true, current_artist_name } if cart has items from a different artist.
   * Call clearAndAddToCart() to replace the cart contents.
   */
  const addToCart = useCallback(async (artwork, quantity = 1) => {
    if (!artwork?.id) {
      logger.error('useSmartCart', 'Invalid artwork object - missing id');
      return;
    }

    // Single-artist guard: check for artist conflict
    const conflict_check = checkArtistConflict(artwork);
    if (conflict_check.conflict) {
      return {
        conflict: true,
        current_artist_name: conflict_check.current_artist_name,
        new_artwork: artwork,
        new_quantity: quantity,
      };
    }

    try {
      if (is_authenticated && user?.id) {
        if (import.meta.env.DEV) {
          console.log('🔐 [SmartCart] Adding to cart (authenticated):', artwork.title);
        }

        const result = await dispatch(addToCartAsync({
          artwork_id: artwork.id,
          quantity
        })).unwrap();

        return { conflict: false, result };
      } else {
        if (import.meta.env.DEV) {
          console.log('👤 [SmartCart] Adding to cart (guest):', artwork.title);
        }

        dispatch(addToCartLocal({ ...artwork, quantity }));
        return { conflict: false };
      }
    } catch (error) {
      console.error('❌ [SmartCart] Error adding to cart:', error);

      if (is_authenticated) {
        console.log('🔄 [SmartCart] Database failed, using local fallback');
        dispatch(addToCartLocal({ ...artwork, quantity }));
      }

      throw error;
    }
  }, [dispatch, is_authenticated, user?.id, checkArtistConflict]);

  /**
   * Clear cart and add new artwork (used after artist conflict confirmation)
   */
  const clearAndAddToCart = useCallback(async (artwork, quantity = 1) => {
    try {
      if (is_authenticated && user?.id) {
        await dispatch(clearCartAsync()).unwrap();
        const result = await dispatch(addToCartAsync({
          artwork_id: artwork.id,
          quantity
        })).unwrap();
        return { conflict: false, result };
      } else {
        dispatch(clearCart());
        dispatch(addToCartLocal({ ...artwork, quantity }));
        return { conflict: false };
      }
    } catch (error) {
      console.error('❌ [SmartCart] Error in clearAndAddToCart:', error);
      throw error;
    }
  }, [dispatch, is_authenticated, user?.id]);

  /**
   * Remove item from cart with smart routing
   * @param {string} artwork_id - Artwork ID to remove
   */
  const removeFromCart = useCallback(async (artwork_id) => {
    if (!artwork_id) {
      console.error('❌ [SmartCart] Invalid artwork ID');
      return;
    }

    try {
      if (is_authenticated && user?.id) {
        // Authenticated user: Remove from database + localStorage
        if (import.meta.env.DEV) {
          console.log('🔐 [SmartCart] Removing from cart (authenticated):', artwork_id);
        }

        // Note: cartPersistenceMiddleware auto-persists to localStorage on fulfilled
        const result = await dispatch(removeFromCartAsync({
          artwork_id
        })).unwrap();

        return result;
      } else {
        // Non-authenticated user: localStorage only
        if (import.meta.env.DEV) {
          console.log('👤 [SmartCart] Removing from cart (guest):', artwork_id);
        }

        // Update Redux state (middleware auto-persists to localStorage)
        dispatch(removeFromCartLocal({ id: artwork_id }));
      }
    } catch (error) {
      console.error('❌ [SmartCart] Error removing from cart:', error);

      // Fallback to local state for authenticated users if DB fails
      // Middleware auto-persists to localStorage on removeFromCartLocal
      if (is_authenticated) {
        console.log('🔄 [SmartCart] Database failed, using local fallback');
        dispatch(removeFromCartLocal({ id: artwork_id }));
      }

      throw error;
    }
  }, [dispatch, is_authenticated, user?.id]);

  /**
   * Update item quantity with optimistic updates
   * @param {string} artwork_id - Artwork ID
   * @param {number} quantity - New quantity
   */
  const updateQuantity = useCallback(async (artwork_id, quantity) => {
    if (!artwork_id || quantity < 0) {
      console.error('❌ [SmartCart] Invalid parameters for quantity update');
      return;
    }

    try {
      if (quantity === 0) {
        // Remove item if quantity is 0
        return removeFromCart(artwork_id);
      }

      // Get original quantity for rollback if needed
      const current_item = items.find(item =>
        (item.artwork?.id === artwork_id) || (item.artwork_id === artwork_id) || (item.id === artwork_id)
      );
      const original_quantity = current_item?.quantity || 1;

      if (is_authenticated && user?.id) {
        // Authenticated user: Optimistic update + database sync
        if (import.meta.env.DEV) {
          console.log('🔐 [SmartCart] Updating quantity optimistically (authenticated):', artwork_id, quantity);
        }

        // 1. Immediate optimistic update
        dispatch(updateQuantityOptimistic({ id: artwork_id, quantity }));

        try {
          // 2. Sync to database in background (Backend Custom API)
          // Note: cartPersistenceMiddleware auto-persists to localStorage on fulfilled
          const result = await dispatch(updateCartItemAsync({
            artwork_id,
            quantity
          })).unwrap();

          // 3. Mark update as successful
          dispatch(updateQuantitySuccess({ id: artwork_id }));

          return result;
        } catch (sync_error) {
          // 5. Rollback optimistic update if sync fails
          console.error('❌ [SmartCart] Database sync failed, rolling back:', sync_error);
          dispatch(updateQuantityFailure({ id: artwork_id, original_quantity }));

          // Fallback to localStorage
          localCartService.updateQuantity(artwork_id, original_quantity);
          throw sync_error;
        }
      } else {
        // Non-authenticated user: localStorage only (immediate)
        if (import.meta.env.DEV) {
          console.log('👤 [SmartCart] Updating quantity (guest):', artwork_id, quantity);
        }

        // Update Redux state (middleware auto-persists to localStorage)
        dispatch(updateQuantityLocal({ id: artwork_id, quantity }));
      }
    } catch (error) {
      console.error('❌ [SmartCart] Error updating quantity:', error);
      throw error;
    }
  }, [dispatch, is_authenticated, user?.id, removeFromCart, items]);

  /**
   * Get cart item count
   */
  const getCartCount = useCallback(() => {
    return items.reduce((total, item) => total + (item.quantity || 1), 0);
  }, [items]);

  /**
   * Get cart total price
   */
  const getCartTotal = useCallback(() => {
    return items.reduce((total, item) => {
      const price = item.artwork?.price || item.price || 0;
      const quantity = item.quantity || 1;
      return total + (price * quantity);
    }, 0);
  }, [items]);

  /**
   * Check if artwork is in cart
   * @param {string} artwork_id - Artwork ID to check
   */
  const isInCart = useCallback((artwork_id) => {
    return items.some(item =>
      (item.artwork?.id === artwork_id) || (item.artwork_id === artwork_id) || (item.id === artwork_id)
    );
  }, [items]);

  /**
   * Get cart item by artwork ID
   * @param {string} artwork_id - Artwork ID
   */
  const getCartItem = useCallback((artwork_id) => {
    return items.find(item =>
      (item.artwork?.id === artwork_id) || (item.artwork_id === artwork_id) || (item.id === artwork_id)
    );
  }, [items]);

  /**
   * Check if item is currently being updated
   * @param {string} artwork_id - Artwork ID
   */
  const isItemUpdating = useCallback((artwork_id) => {
    return updatingItems.includes(artwork_id);
  }, [updatingItems]);

  return {
    // Core actions
    addToCart,
    clearAndAddToCart,
    removeFromCart,
    updateQuantity,

    // State getters
    items,
    loading: loading,
    cartLoading: loading,
    error,
    is_authenticated,

    // Computed values
    cartCount: getCartCount(),
    cartTotal: getCartTotal(),

    // Utility functions
    isInCart,
    getCartItem,
    isItemUpdating
  };
};

export default useSmartCart;